import { differenceInSeconds, format } from "date-fns"
import { parse } from "node-html-parser"
import {
  type AnyVanLocale,
  type PlacesLocale,
  LOCALE,
  LOCALE_TO_PLACES_LOCALE,
  PLACES_LOCALE
} from "~/constants/locale"
import type { Dictionary } from "vue-router/types/router"
import type { LinkPropertyHref } from "vue-meta/types/vue-meta"
import type { RouteLocationNormalizedGeneric } from "#vue-router"

/**
 * Function to convert date into time ago
 *
 * @param date string
 * @return string
 */
export function timeAgo(date: string): string {
  if (!date) {
    return "No date provided"
  }

  const periods = ["second", "minute", "hour", "day", "week", "month", "year", "decade"]
  const lengths = ["60", "60", "24", "7", "4.35", "12", "10"]
  const dateObj = new Date(date)
  const nowObj = new Date()

  let difference = 0
  let tense = "ago"
  // is it future date or past date
  if (nowObj > dateObj) {
    difference = differenceInSeconds(nowObj, dateObj)
  } else {
    difference = differenceInSeconds(dateObj, nowObj)
    tense = "from now"
  }

  let j = 0
  for (j = 0; difference >= parseInt(lengths[j]) && j < lengths.length - 1; j++) {
    difference /= parseInt(lengths[j])
  }

  difference = Math.round(difference)

  if (difference != 1) {
    periods[j] += "s"
  }

  return `${difference} ${periods[j]} ${tense}`
}

/**
 * Formats a date with an ordinal day number (e.g., "21st August 2003")
 * @param {Date} date - The date to format
 * @returns {string} The formatted date string with ordinal day number
 * @example
 * const date = new Date('2003-08-21');
 * formatDateWithOrdinal(date); // Returns "21st August 2003"
 */
export const formatDateWithOrdinal = (
  date: Date,
  localeIso: string,
  localeCode: AnyVanLocale
): string => {
  let parts: Intl.DateTimeFormatPart[] = []
  try {
    parts = new Intl.DateTimeFormat(localeIso, {
      day: "numeric",
      month: "long",
      year: "numeric"
    }).formatToParts(date)
  } catch (error) {
    console.error("Error formatting date:", error)
  }

  /**
   * Adds an ordinal suffix to a number (e.g., 1st, 2nd, 3rd, 4th)
   * @param {number} n - The number to add an ordinal suffix to
   * @param {AnyVanLocale} locale The locale
   * @returns {string} The number with its ordinal suffix
   */
  const ordinal = (n: number, locale: AnyVanLocale = "en"): string => {
    switch (locale) {
      case LOCALE.ES:
        return getOrdinalSpanish(n)
      case LOCALE.FR:
        return getOrdinalFrench(n)
      case LOCALE.DE:
        return getOrdinalGerman(n)
      default:
        return getOrdinalEnglish(n)
    }
  }

  const getOrdinalEnglish = (n: number): string => {
    const s: string[] = ["th", "st", "nd", "rd"]
    const v: number = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
  }

  const getOrdinalSpanish = (n: number): string => {
    return `${n} de`
  }

  const getOrdinalFrench = (n: number): string => {
    if (n === 1) {
      return "1er"
    }
    return `${n}e`
  }

  const getOrdinalGerman = (n: number): string => {
    return `${n}.`
  }

  const day = parseInt(parts.find(part => part.type === "day")?.value || "0")
  const month = parts.find(part => part.type === "month")?.value || ""
  const year = parts.find(part => part.type === "year")?.value || ""

  return `${ordinal(day, localeCode)} ${month} ${year}`
}
/**
 * Function to convert date to a timestamp
 *
 * @param date Date | string
 * @return string
 */
export function formatDateForTimestamp(date?: Date | string): string {
  if (typeof date === "string" || date instanceof String) {
    date = new Date(date)
  }
  return format(date ?? new Date(), "yyyy-MM-dd HH:mm:ss")
}

/**
 * Function to get innerText from an HTML string
 * @param html string
 * @return string
 */
export function getInnerText(html: string): string {
  if (!html) return ""
  const textContentRegex = /<[^>]*>([^<]*)<\/[^>]*>/g
  const matches = html.match(textContentRegex)

  if (!matches) {
    return ""
  }

  return matches
    .map(match => match.replace(textContentRegex, "$1"))
    .join(" ")
    .trim()
}

/**
 * Function to extract the link from WordPress HTML data
 */
export function extractHref(htmlString: string): string | null {
  if (!htmlString) return null
  const pattern = /href=["']([^"']+)["']/
  const match = htmlString.match(pattern)
  return match ? match[1] : null
}

/**
 * Function to extract the image source from WordPress HTML data
 */
export function extractImageUrl(htmlString: string): string | null {
  const regex = /src="([^"]*)"/
  const match = htmlString.match(regex)
  return match ? match[1] : null
}

export function getScreenWidth() {
  if (typeof window !== "undefined") {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  }
  return null
}

export function getV4Host(host: string, locale: string): string {
  switch (locale) {
    case LOCALE.UK:
      return host
    case "ie":
      return host.replace(".com", ".ie")
    case LOCALE.US:
      return host.replace(".com", ".us")
    case LOCALE.DE:
      return host.replace(".com", ".de")
    case "it":
      return host.replace(".com", ".it")
    case LOCALE.FR:
      return host.replace(".com", ".fr")
    case LOCALE.ES:
      return host.replace(".com", ".es")
    default:
      return host
  }
}

export function getV4Home(host: string, locale: string): string {
  switch (locale) {
    case LOCALE.US:
      return host + "/us"
    case LOCALE.ES:
      return host + "/es"
    case LOCALE.FR:
      return host + "/fr"
    case LOCALE.DE:
      return "https://www.anyvan.de"
      //return host + "/de"
    default:
      return getV4Host(host, locale)
  }
}

export function getv4LocaleFromI18nLocale(i18nLocale: string): string {
  switch (i18nLocale) {
    case LOCALE.UK:
      return "en-gb"
    case "ie":
      return "en-ie"
    case LOCALE.US:
      return "en-us"
    case LOCALE.ES:
      return "es-es"
    case "fr":
      return "fr-fr"
    case "de":
      return "de-de"
    case "it":
      return "it-it"
    default:
      return "en-gb"
  }
}

export function getPath(route: RouteLocationNormalizedGeneric): string {
  return route.path ? route.path.replace(/\/$/, "") : ""
}

export function getLoginPage(i18nLocale: string): string {
  switch (i18nLocale) {
    case LOCALE.US:
      return "login-with-credentials"
    case LOCALE.ES:
      return "iniciar-sesion"
    case LOCALE.FR:
      return "connexion"
    case LOCALE.DE:
      return "einloggen"
    default:
      return ""
  }
}

export function isHomePage(i18nLocale: string, path: string): boolean {
  switch (i18nLocale) {
    case LOCALE.UK:
      return path === "/home"
    case LOCALE.US:
      return path === "/us/home"
    case LOCALE.ES:
      return path === "/es/inicio"
    case LOCALE.FR:
      return path === "/fr/accueil"
    case LOCALE.DE:
      // TODO for DE
    default:
      return false
  }
}

export function isTPPage(i18nLocale: AnyVanLocale, path: string): boolean {
  switch (i18nLocale) {
    case LOCALE.UK:
      return path === "/become-a-transport-partner"
    case LOCALE.US:
      return path === "/us/become-an-anyvan-shipping-partner"
    case LOCALE.ES:
      return path === "/es/transporta-para-anyvan"
    case LOCALE.FR:
      return path === "/fr/devenir-transporteur-anyvan"
    case LOCALE.DE:
      // TODO for DE
    default:
      return false
  }
}

export function isResponseIQExcludePage(i18nLocale: AnyVanLocale, path: string): boolean {
  const excludePaths: Record<AnyVanLocale, { exact: string[]; startsWith: string[] }> = {
    [LOCALE.UK]: {
      exact: [
        "",
        "/home",
        "/about-us",
        "/contact-us",
        "/privacy-policy",
        "/terms-and-conditions",
        "/become-a-transport-partner"
      ],
      startsWith: ["/blog", "/storage"]
    },
    [LOCALE.ES]: {
      exact: [
        "/es",
        "/es/inicio",
        "/es/sobre-nosotros",
        "/es/contactanos",
        "/es/politica-de-privacidad",
        "/es/terminos-y-condiciones",
        "/es/transporta-para-anyvan"
      ],
      startsWith: []
    },
    [LOCALE.FR]: {
      exact: [
        "/fr",
        "/fr/accueil"
      ],
      startsWith: []
    },
    [LOCALE.DE]: {
      exact: [
        "/de"
      ],
      startsWith: []
    }
  }
}

export function getHrefLangLinkMetaForHomePage(): LinkPropertyHref[] {
  return [
    {
      rel: "alternate",
      hreflang: "en-gb",
      href: "https://www.anyvan.com/"
    },
    {
      rel: "alternate",
      hreflang: "en-us",
      href: "https://www.anyvan.com/us"
    },
    {
      rel: "alternate",
      hreflang: "es",
      href: "https://www.anyvan.com/es"
    },
    {
      rel: "alternate",
      hreflang: "x-default",
      href: "https://www.anyvan.com/"
    }
  ]
}

export function getHrefLangLinkMetaForTPPage(): LinkPropertyHref[] {
  return [
    {
      rel: "alternate",
      hreflang: "en-gb",
      href: "https://www.anyvan.com/become-a-transport-partner"
    },
    {
      rel: "alternate",
      hreflang: "en-us",
      href: "https://www.anyvan.com/us/become-an-anyvan-shipping-partner"
    },
    {
      rel: "alternate",
      hreflang: "es",
      href: "https://www.anyvan.com/es/transporta-para-anyvan"
    },
    {
      rel: "alternate",
      hreflang: "x-default",
      href: "https://www.anyvan.com/become-a-transport-partner"
    }
  ]
}

/**
 * RegEx explanation
 * [a-zA-Z]+:\/\/ Match any protocol
 * [a-zA-Z0-9-]+ Match the first subdomain, e.g. cms-internal
 * (?:\.[a-zA-Z0-9-]+)* Match the domain, e.g. .anyvan. Also, will match any number of subdomains, e.g. dev.cms.internal
 * \.[a-zA-Z]{2,} Match the top-level domain (TLD), e.g. .com
 */
export const matchProtocolAndHost = new RegExp(
  /[a-zA-Z]+:\/\/[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}/g
)

export const replaceUrlS3Helper = (
  url: string | null,
  destinationHost: string,
  graphqlEndpoint: string
): string => {
  if (!url) {
    return ""
  }

  if (
    graphqlEndpoint.includes("cms.anyvan.local") ||
    graphqlEndpoint.includes("dev-cms.anyvan.com")
  ) {
    return url
  }
  if (url.includes("/app/uploads")) {
    // WordPress media library images that have been auto-uploaded to S3
    return url
      .replace(matchProtocolAndHost, destinationHost)
      .replace(/\/app\/uploads/g, "/content-uploads")
  }

  if (url.includes("/app/themes/anyvan")) {
    // WordPress theme images
    return url
      .replace(matchProtocolAndHost, destinationHost)
      .replace(/\/app\/themes\/anyvan\/assets\/img/g, "/content-static/wp-theme")
  }

  return url
}

/**
 * Converts a blob of HTML to pure text that is easily consumed by a human.
 * The original use was to ingest HTML from the CMS and format it into
 * schema.org metadata. This metadata will end up in front of a user/lead,
 * so it has to be formatted nicely with spaces where you expect etc.
 *
 * @param text
 */
export function prettyStripHTML(text: string): string {
  const root = parse(text)

  // Add periods to list items that don't have them
  const listItems = root.querySelectorAll("li")
  listItems.forEach(item => {
    const text = item.textContent.trim()
    if (!text.endsWith(".") && !text.endsWith("!") && !text.endsWith("?")) {
      item.set_content(text + ".")
    }
  })

  // Add spaces after paragraphs and before lists
  const paragraphs = root.querySelectorAll("p")
  const lists = root.querySelectorAll("ul, ol")
  ;[...paragraphs, ...lists].forEach(el => {
    el.insertAdjacentHTML("afterend", " ")
  })

  let content = root.textContent

  // Clean up whitespace
  content = content
    .replace(/[\r\n\t]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim()

  // Replace any potential JSON-escape-prone characters
  return content.replace(/'/g, "'").replace(/"/g, '"')
}

/**
 * Replace placeholders in CMS blocks with dynamic content.
 *
 * @param {Block} block - The CMS block object to process.
 * @param {Object.<string, string>} replacementData - Data to dynamically inject
 * into CMS blocks.
 *
 * @returns {Block} A new block object with placeholders replaced in `innerHtml`, `attributes`, and
 * recursively in `innerBlocks`.
 */
export const processBlock = (block: Block, replacementData: Dictionary<any>): Block => {
  const processedBlock = { ...block }

  if (block.innerHtml) {
    processedBlock.innerHtml = block.innerHtml.replace(/\{([^}]+)\}/g, (match, key) => {
      const propertyKey = key.toLowerCase()
      return replacementData[propertyKey] || match
    })
  }

  if (block.attributes) {
    processedBlock.attributes = block.attributes.map(attr => ({
      ...attr,
      value: attr.value.replace(/\{([^}]+)\}/g, (match, key) => {
        const propertyKey = key.toLowerCase()
        return replacementData[propertyKey] || match
      })
    }))
  }

  if (block.innerBlocks && block.innerBlocks.length) {
    processedBlock.innerBlocks = block.innerBlocks.map(innerBlock =>
      processBlock(innerBlock, replacementData)
    )
  }

  return processedBlock
}

export const swapFirstTwoElements = (array: any[]): any[] => {
  const newArray = [...array]
  ;[newArray[0], newArray[1]] = [newArray[1], newArray[0]]
  return newArray
}

export const getRegion = (
  areaLevel1: string,
  areaLevel2: string,
  country: PlacesLocale = ""
): string => {
  const regionIsAreaLevel2Countries = [PLACES_LOCALE.UK, PLACES_LOCALE.US]

  if (regionIsAreaLevel2Countries.includes(country)) {
    return areaLevel2
  } else {
    return areaLevel1
  }
}

export const formatAddress = (
  streetName: string,
  streetNumber: string,
  country: PlacesLocale = ""
): string => {
  const numberFirstCountries = [PLACES_LOCALE.UK, PLACES_LOCALE.US, PLACES_LOCALE.FR]
  const nameFirstCountries = [PLACES_LOCALE.ES, PLACES_LOCALE.DE]

  if (!streetName) {
    return ""
  }

  if (streetNumber && numberFirstCountries.includes(country)) {
    return `${streetNumber} ${streetName}`
  } else if (streetNumber && nameFirstCountries.includes(country)) {
    return `${streetName} ${streetNumber}`
  } else {
    return `${streetName}`
  }
}

export const isSameLocaleAsPlacesLocale = (locale: AnyVanLocale, placesLocale: PlacesLocale) => {
  return LOCALE_TO_PLACES_LOCALE[locale] === placesLocale
}

export const waitingForPlace = async (waitForPlace: Ref<boolean>): Promise<void> => {
  for (let i = 0; i < 10; i++) {
    await new Promise(resolve => setTimeout(resolve, 500))
    if (!waitForPlace.value) break
  }
}
